import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { MenuItem } from 'primeng/api';
import { GenerateCvService } from 'src/app/pages/generate resume/generate-cv-steps/services/generate-cv/generate-cv.service';
import { ImageService } from 'src/app/services/image/image.service';
import { PaymentService } from 'src/app/services/payment/payment.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-generate-linkedin-profile',
  templateUrl: './generate-linkedin-profile.component.html',
  styleUrls: ['./generate-linkedin-profile.component.css'],
})
export class GenerateLinkedinProfileComponent implements OnInit {
  @ViewChild('stepsSection') stepsSection!: ElementRef;

  services = [
    {
      color: 'blue',
      img: '../../../assets/icon/headline.svg',
      title: 'Headline',
      description: [
        'File format and size',
        'Resume length',
        'Long bullet points with suggestions on how to shorten',
      ],
    },
    {
      color: 'pink',
      img: '../../../assets/icon/summary.svg',
      title: 'Summary',
      description: [
        'Contact information',
        'Essential sections',
        'Personality showcase with tips on how to improve',
      ],
    },
    {
      color: 'blue',
      img: '../../../assets/icon/contact.svg',
      title: 'Contacts',
      description: [
        'ATS parse rate',
        'Repetition of words and phrases',
        'Spelling and grammar',
        'Quantifying impact in experience section with examples',
      ],
    },
    {
      color: 'pink',
      img: '../../../assets/icon/experience.png',
      title: 'Experience',
      description: ['Hard skills', 'Soft skills'],
    },
    {
      color: 'blue',
      img: '../../../assets/icon/educatiion.svg',
      title: 'Education',
      description: [
        'Resume design',
        'Email address',
        'Usage of active voice',
        'Usage of buzzwords and cliches',
      ],
    },
  ];

  steps: MenuItem[] = [];
  activeIndex: number = 0;
  linkedinFile: any;
  cvFile: File | null = null;
  loading: boolean = false;
  userId!: any;
  serviceSystemId: string = '';
  isWordPurchased: boolean = false;
  loadingPurchaseStatus: boolean = true;
  linkedinUrlImage: string = '';

  constructor(
    private translate: TranslateService,
    private cvService: GenerateCvService,
    private paymentService: PaymentService,
    private router: Router,
    private gtmService: GoogleTagManagerService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private imageService: ImageService,
  ) {
    this.userId = localStorage.getItem('userId');
    this.getTranslate();
    this.translate.onLangChange.subscribe(() => {
      this.getTranslate();
    });
  }

  ngOnInit() {
    this.userId = localStorage.getItem('userId');
    this.serviceSystemId =
      localStorage.getItem('linkedinServiceSystemId') || '';

    if (this.serviceSystemId) {
      this.activeIndex = 1;
      this.loadLinkedinDataFromApi();
    }

    const pendingFile = sessionStorage.getItem('pendingLinkedinFile');
    const pendingFileName = sessionStorage.getItem('pendingLinkedinFileName');
    const pendingFileType = sessionStorage.getItem('pendingLinkedinFileType');

    if (pendingFile && pendingFileName && pendingFileType && this.userId) {
      sessionStorage.removeItem('pendingLinkedinFile');
      sessionStorage.removeItem('pendingLinkedinFileName');
      sessionStorage.removeItem('pendingLinkedinFileType');

      const byteString = atob(pendingFile);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: pendingFileType });
      this.linkedinFile = new File([blob], pendingFileName, {
        type: pendingFileType,
      });

      setTimeout(() => this.scrollToSteps(), 300);
      this.processFile();
    }
  }

  getTranslate() {
    this.translate
      .get(['Upload LinkedIn', 'Result'])
      .subscribe((translations) => {
        this.steps = [
          { label: translations['Upload LinkedIn'] },
          { label: translations['Result'] },
        ];
      });
  }

  scrollToSteps() {
    if (this.stepsSection && this.stepsSection.nativeElement) {
      this.stepsSection.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }

  private saveFileToPending(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        sessionStorage.setItem('pendingLinkedinFile', base64);
        sessionStorage.setItem('pendingLinkedinFileName', file.name);
        sessionStorage.setItem('pendingLinkedinFileType', file.type);
        resolve();
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // ✅ Just stores the file — no API call
  onLinkedinSelected(event: any) {
    this.linkedinFile = event.target.files[0];
    if (this.linkedinFile && !this.userId) {
      this.saveFileToPending(this.linkedinFile).then(() => {
        localStorage.setItem('redirectUrl', '/create-linkedin-profile');
        this.router.navigate(['/login']);
      });
    }
  }

  // ✅ Just stores the CV file — no API call
  onCvSelected(event: any) {
    const file: File = event.target.files[0];
    if (file) {
      this.cvFile = file;
    }
  }

  // ✅ Called when user clicks "Generate" button — sends both files together
  onGenerate() {
    if (!this.linkedinFile) return;
    this.processFile();
  }

  processFile() {
    this.loading = true;
    this.activeIndex = 1;

    const data = new FormData();
    data.append('linkedin', this.linkedinFile);

    if (this.cvFile) {
      data.append('cv', this.cvFile);
    }

    this.cvService.enhanceLinkedinCv(data).subscribe({
      next: (response: any) => {
        const serviceId =
          response.headers.get('x-service-id') ||
          response.headers.get('X-Service-Id');

        if (serviceId) {
          this.serviceSystemId = serviceId;
          localStorage.setItem('linkedinServiceSystemId', serviceId);
        }
        const blobUrl = URL.createObjectURL(response.body);
        this.linkedinUrlImage = blobUrl;
        this.loading = false;
      },
      error: (err) => {
        console.error('Error enhancing LinkedIn profile:', err);
        this.activeIndex = 0;
        this.loading = false;
      },
    });
  }

  onLinkedinDrop(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      this.linkedinFile = event.dataTransfer.files[0];
      if (!this.userId) {
        this.saveFileToPending(this.linkedinFile).then(() => {
          localStorage.setItem('redirectUrl', '/create-linkedin-profile');
          this.router.navigate(['/login']);
        });
      }
      // Don't auto-process — user clicks Generate
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

loadLinkedinDataFromApi() {
  if (!this.serviceSystemId) return;

  this.loadingPurchaseStatus = true;

  this.paymentService
    .checkIsGeneratedLinkedinPurchased(this.serviceSystemId)
    .subscribe({
      next: (res: any) => {
        this.isWordPurchased = res.isPaid || false;
        const imagePath = res.imagePath || null;
        if (imagePath) {
          this.loadLinkedinImage(imagePath);
        }
        this.loadingPurchaseStatus = false;
      },
      error: (err) => {
        console.error(err);
        this.loadingPurchaseStatus = false;
      },
    });
}
loadLinkedinImage(imagePath: string) {
  if (imagePath) {
    this.linkedinUrlImage = 'assets/img/placeholder.png';
    const fullImageUrl = `${environment.apiUrl}${imagePath}?t=${new Date().getTime()}`;

    this.imageService.getSecureImage(fullImageUrl).subscribe({
      next: (blobUrl) => {
        this.linkedinUrlImage = blobUrl;
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading image:', err);
        this.linkedinUrlImage = 'assets/img/error-placeholder.png';
        this.loading = false;
      },
    });
  }
}

  goToPayment(productKey: string) {
    // Clear saved state so user starts fresh when returning to this page
    localStorage.removeItem('linkedinServiceSystemId');
    localStorage.removeItem('linkedinImagePath');

    this.router.navigate(['/payment'], {
      state: {
        productKey: productKey,
        serviceSystemId: this.serviceSystemId,
      },
      queryParams: {
        productKey: productKey,
        serviceSystemId: this.serviceSystemId,
      },
    });
  }

  downloadFile(base64Data: string, filename: string) {
    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      const binaryData = this.base64ToArrayBuffer(base64Data);
      const blob = new Blob([binaryData], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const link = document.createElement('a');
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      this.sendGoogleEvent('download linkedin profile');
    }
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    let binaryString = '';
    if (typeof window !== 'undefined') {
      binaryString = window.atob(base64);
    }
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  sendGoogleEvent(name: string) {
    this.googleAnalyticsService.event(name);
    this.gtmService.pushTag({
      event: 'customEvent',
      category: 'button',
      action: 'click',
      label: name,
    });
  }

  onDownloadWord() {
    if (!this.isWordPurchased) {
      this.goToPayment('enhance-linkedin');
      return;
    }

    this.loadingPurchaseStatus = true;

    this.cvService.downloadLinkedinProfile(this.serviceSystemId).subscribe({
      next: (response: any) => {
        if (response && response.data) {
          this.downloadFile(response.data, 'LinkedInProfile.docx');
        }
        this.loadingPurchaseStatus = false;
      },
      error: (err) => {
        console.error('Error downloading LinkedIn profile:', err);
        this.loadingPurchaseStatus = false;
      },
    });
  }

  createNewLinkedinProfile() {
    this.activeIndex = 0;
    this.serviceSystemId = '';
    this.linkedinUrlImage = '';
    this.isWordPurchased = false;
    this.cvFile = null;
    localStorage.removeItem('linkedinServiceSystemId');
    localStorage.removeItem('linkedinImagePath');
  }
}